import { Option } from '@sapphire/result';
import { match } from 'ts-pattern';

import {
  canSeeTargetMargin,
  Survey,
  SURVEY_WORKFLOW_PAGE,
  SurveyKind,
  SurveyStatusInfo,
  SurveyWorkflowPage,
} from '@ps/survey-utils';
import { BuyerSettings } from '@ps/user-settings-data-access';

export const INSIGHT = {
  // Includes the flexibility (CPI) pricing it is priced on.
  FEASIBILITY: 'feasibility',
  SCORE: 'score',
  // Supplier allocations seed and blends.
  SUPPLIERS: 'suppliers',
  PURE_PRICE: 'pure-price',
  EPC_PLATFORM: 'epc-platform',
  EPC_ESTIMATE: 'epc-estimate',
  EPC_ACTUAL: 'epc-actual',
  SIMILAR_SURVEYS: 'similar-surveys',
  // The tags catalog and the survey's own linked tags.
  TAGS: 'tags',
  IMPROBABLE_QUOTAS: 'improbable-quotas',
  UNUSED_QUALIFICATION_NOTICE: 'unused-qualification-notice',
  // A plain save re-running the insights above.
  SAVE_REFRESH: 'save-refresh',
} as const;

export type Insight = (typeof INSIGHT)[keyof typeof INSIGHT];

// Everything a rule is checked against. Inside the survey workflow the viewer
// is always a buyer, or an operator impersonating one (see
// surveyWorkflowAccessGuard).
export interface InsightContext {
  readonly page: Option<SurveyWorkflowPage>;
  readonly survey: Option<Survey>;
  readonly hasImpersonatedBuyer: boolean;
  readonly buyerSettings: BuyerSettings;
}

const { CREATE, EDIT, LAUNCH } = SURVEY_WORKFLOW_PAGE;

// The insights a buyer setting switches on, so they can only be decided once
// the settings load. Keep it in step with `isInsightAllowed`: the config spec
// fails when an insight's answer depends on a setting but is missing here.
const BUYER_SETTING_INSIGHTS: readonly Insight[] = [
  INSIGHT.SCORE,
  INSIGHT.EPC_PLATFORM,
  INSIGHT.EPC_ESTIMATE,
  INSIGHT.EPC_ACTUAL,
  INSIGHT.SIMILAR_SURVEYS,
  INSIGHT.TAGS,
];

export function readsBuyerSetting(insight: Insight): boolean {
  return BUYER_SETTING_INSIGHTS.includes(insight);
}

/**
 * WHERE, for WHOM and behind WHICH buyer setting each insight may call its API.
 * A page not named here never calls it. The launch page shows estimates only in
 * the children drawer table: its rows load, and a row's own CPI/completes edit
 * refreshes that row.
 */
export function isInsightAllowed(
  insight: Insight,
  { page, survey, hasImpersonatedBuyer, buyerSettings }: InsightContext,
): boolean {
  const onPage = (...pages: SurveyWorkflowPage[]): boolean =>
    page.mapOr(false, (current) => pages.includes(current));
  // A missing survey (pre-draft, or a child whose load failed) is not launched.
  const isDraft = SurveyStatusInfo.forOptional(survey).isDraftOrAbsent();
  const onEditDraft = onPage(EDIT) && isDraft;
  const mayPriceAsService = canSeeTargetMargin({
    hasImpersonatedBuyer,
    hasTargetMargin: buyerSettings.enableTargetMargin,
  });

  return match(insight)
    .with(
      INSIGHT.FEASIBILITY,
      () =>
        onEditDraft ||
        (onPage(LAUNCH) && isDraft && survey.mapOr(false, hasChildrenDrawer)),
    )
    .with(
      INSIGHT.SCORE,
      () => onEditDraft && !!buyerSettings.enableRYGSurveyScore,
    )
    .with(
      INSIGHT.SUPPLIERS,
      INSIGHT.PURE_PRICE,
      INSIGHT.UNUSED_QUALIFICATION_NOTICE,
      INSIGHT.SAVE_REFRESH,
      () => onEditDraft,
    )
    .with(
      INSIGHT.EPC_PLATFORM,
      INSIGHT.EPC_ESTIMATE,
      () => (onPage(CREATE) || onEditDraft) && mayPriceAsService,
    )
    .with(INSIGHT.EPC_ACTUAL, () => onEditDraft && mayPriceAsService)
    .with(
      INSIGHT.SIMILAR_SURVEYS,
      () => onEditDraft && !!buyerSettings.enableSimilarSurveys,
    )
    .with(
      INSIGHT.TAGS,
      () => onPage(CREATE, EDIT) && !!buyerSettings.enableSurveyTags,
    )
    .with(INSIGHT.IMPROBABLE_QUOTAS, () => onPage(EDIT))
    .exhaustive();
}

function hasChildrenDrawer(survey: Survey): boolean {
  return SurveyKind.isTraditionalMc(survey) || SurveyKind.isGroupMc(survey);
}
